/**
 * Authentication Context
 * Manages user authentication state and token
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getTelegramUser, getInitData } from '../utils/telegram';

interface User {
  id: number;
  username?: string;
  firstName: string;
  lastName?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  useEffect(() => {
    // Always try to authenticate with fresh Telegram initData when available
    // This ensures tokens are refreshed and language/user data stays in sync
    const attemptLogin = async () => {
      // Wait a bit for Telegram WebApp to initialize
      await new Promise(resolve => setTimeout(resolve, 100));

      const initData = getInitData();
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');

      // If we have fresh Telegram initData, always re-authenticate
      // This ensures the token is valid and syncs user data from database
      if (initData) {
        try {
          console.log('Fresh Telegram initData available, authenticating...');
          await login();
          return;
        } catch (error) {
          console.warn('Authentication with fresh initData failed:', error);
          // Fall through to try cached token
        }
      }

      // Fallback to cached token if no initData (e.g., testing in browser)
      if (savedToken && savedUser) {
        console.log('Using cached token (no fresh initData)');
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        setIsLoading(false);
      } else {
        console.warn('No authentication available');
        setIsLoading(false);
      }
    };

    attemptLogin();
  }, []);

  const login = async () => {
    try {
      setIsLoading(true);

      const telegramUser = getTelegramUser();
      const initData = getInitData();

      // Get bot username from URL path (e.g., /shop/MyBot/...)
      const pathParts = window.location.pathname.split('/');
      const shopIndex = pathParts.indexOf('shop');
      const botUsername = shopIndex >= 0 ? pathParts[shopIndex + 1] : null;

      if (!botUsername) {
        console.warn('Bot username not found in URL path');
        throw new Error('Bot username not found in URL');
      }

      // Need either initData or user data for authentication
      if (!initData && !telegramUser) {
        console.warn('No Telegram data available for authentication');
        throw new Error('Telegram data not available');
      }

      console.log('Attempting Telegram authentication...', {
        hasInitData: !!initData,
        hasUser: !!telegramUser,
        botUsername
      });

      // Authenticate with backend using botUsername (bot token is looked up on backend)
      const response = await axios.post(`${API_URL}/api/auth/telegram`, {
        initData,
        botUsername
      });

      const { token: authToken, user: userData } = response.data;

      // Save to state and localStorage
      setToken(authToken);
      setUser(userData);
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));

      // Set default axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      console.log('Authentication successful');

    } catch (error: any) {
      console.error('Login failed:', error?.response?.data || error?.message || error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshToken = async () => {
    try {
      if (!token) return;

      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        token
      });

      const { token: newToken } = response.data;

      setToken(newToken);
      localStorage.setItem('auth_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    refreshToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
